import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';
import { AppDataProvider } from './store';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import AssetList from './components/AssetList';
import AssetDetail from './components/AssetDetail';
import ObligationDetail from './components/ObligationDetail';
import ObligationForm from './components/ObligationForm';
import ProjectList from './components/ProjectList';
import ProjectDetail from './components/ProjectDetail';
import ProjectForm from './components/ProjectForm';
import CalendarView from './components/CalendarView';
import SearchView from './components/SearchView';
import SettingsView from './components/SettingsView';

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (session === undefined) {
    return <div className="loading-screen">Loading…</div>;
  }

  if (!session) {
    return <Auth />;
  }

  return (
    <AppDataProvider>
      <Shell />
    </AppDataProvider>
  );
}

function Shell() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const google = params.get('google');
    if (google) {
      navigate('/settings', { replace: true });
    }
  }, [navigate]);

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">🏡</span>
          Lyman Household App
        </div>
        <nav className="topnav">
          <NavLink to="/" end>
            Today
          </NavLink>
          <NavLink to="/assets">Assets</NavLink>
          <NavLink to="/projects">Projects</NavLink>
          <NavLink to="/calendar">Calendar</NavLink>
          <NavLink to="/search">Search</NavLink>
          <NavLink to="/settings">Settings</NavLink>
        </nav>
        <button className="btn-secondary signout" onClick={handleSignOut}>
          Sign out
        </button>
      </header>

      <main className="content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/assets" element={<AssetList />} />
          <Route path="/assets/:assetId" element={<AssetDetail />} />
          <Route path="/obligations/new" element={<ObligationForm />} />
          <Route path="/obligations/:obligationId" element={<ObligationDetail />} />
          <Route path="/obligations/:obligationId/edit" element={<ObligationForm />} />
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/new" element={<ProjectForm />} />
          <Route path="/projects/:projectId" element={<ProjectDetail />} />
          <Route path="/projects/:projectId/edit" element={<ProjectForm />} />
          <Route path="/calendar" element={<CalendarView />} />
          <Route path="/search" element={<SearchView />} />
          <Route path="/settings" element={<SettingsView />} />
        </Routes>
      </main>

      <nav className="bottomnav">
        <NavLink to="/" end>
          <span>🏠</span>Today
        </NavLink>
        <NavLink to="/assets">
          <span>📦</span>Assets
        </NavLink>
        <NavLink to="/projects">
          <span>🛠️</span>Projects
        </NavLink>
        <NavLink to="/obligations/new" className="bottomnav-add">
          <span>＋</span>Add
        </NavLink>
        <NavLink to="/calendar">
          <span>📅</span>Calendar
        </NavLink>
        <NavLink to="/search">
          <span>🔍</span>Search
        </NavLink>
      </nav>
    </div>
  );
}
